import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CloudDownload,
  CloudUpload,
  CheckCircle,
  Warning,
  Info,
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { toast } from 'react-toastify';

interface BackupDialogProps {
  open: boolean;
  onClose: () => void;
}

const BackupDialog: React.FC<BackupDialogProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const handleExportBackup = async () => {
    setLoading(true);
    try {
      // Fetch all payment links
      const paymentLinksRef = collection(db, 'paymentLinks');
      const snapshot = await getDocs(paymentLinksRef);
      
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Timestamps to ISO strings for JSON
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString(),
        expiryDate: doc.data().expiryDate?.toDate?.()?.toISOString(),
        depositDueDate: doc.data().depositDueDate?.toDate?.()?.toISOString(),
        invoiceDueDate: doc.data().invoiceDueDate?.toDate?.()?.toISOString(),
        visitedAt: doc.data().visitedAt?.toDate?.()?.toISOString(),
        lastVisitedAt: doc.data().lastVisitedAt?.toDate?.()?.toISOString(),
        lastPaymentUpdate: doc.data().lastPaymentUpdate?.toDate?.()?.toISOString(),
        editedAt: doc.data().editedAt?.toDate?.()?.toISOString(),
        payments: doc.data().payments?.map((p: any) => ({
          ...p,
          timestamp: p.timestamp?.toDate?.()?.toISOString(),
        })),
      }));

      // Create backup object with metadata
      const backup = {
        exportedAt: new Date().toISOString(),
        exportedBy: 't_bugra@hotmail.com',
        version: '1.0',
        totalRecords: data.length,
        data: data,
      };

      // Create JSON file and download
      const json = JSON.stringify(backup, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `paymentlinks-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Backup exported successfully! ${data.length} records saved.`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export backup');
    } finally {
      setLoading(false);
    }
  };

  const handleImportBackup = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;

      setRestoring(true);
      try {
        const text = await file.text();
        const backup = JSON.parse(text);

        // Validate backup structure
        if (!backup.data || !Array.isArray(backup.data)) {
          throw new Error('Invalid backup format');
        }

        toast.info(`Backup contains ${backup.totalRecords} records. This is a read-only preview.`);
        
        // For safety, we'll just show the data, not actually restore it
        // You can uncomment the restore logic below if you want to enable full restore
        
        /*
        // Restore logic (DANGEROUS - use with caution)
        const batch = writeBatch(db);
        backup.data.forEach((item: any) => {
          const docRef = doc(db, 'paymentLinks', item.id);
          // Convert ISO strings back to Timestamps
          const data = {
            ...item,
            createdAt: item.createdAt ? Timestamp.fromDate(new Date(item.createdAt)) : null,
            // ... convert other date fields
          };
          batch.set(docRef, data);
        });
        await batch.commit();
        toast.success('Backup restored successfully!');
        */

        toast.warning('Restore feature is disabled for safety. Contact developer to enable.');
      } catch (error) {
        console.error('Import error:', error);
        toast.error('Failed to import backup');
      } finally {
        setRestoring(false);
      }
    };
    input.click();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CloudDownload color="primary" />
          <Typography variant="h6">Backup & Restore</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Superuser Tools (t_bugra@hotmail.com only)
          </Typography>
          <Typography variant="caption">
            Export all payment links to JSON or restore from a previous backup.
          </Typography>
        </Alert>

        <List>
          <ListItem>
            <ListItemIcon>
              <CheckCircle color="success" />
            </ListItemIcon>
            <ListItemText
              primary="Export Backup"
              secondary={`Download all payment links as JSON file`}
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <Warning color="warning" />
            </ListItemIcon>
            <ListItemText
              primary="Import Backup"
              secondary="Restore from JSON file (read-only preview)"
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <Info color="info" />
            </ListItemIcon>
            <ListItemText
              primary="Data Included"
              secondary="All fields, payments, timestamps, and metadata"
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 2 }} />

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="caption">
            <strong>Note:</strong> Restore functionality is disabled by default for safety. 
            Backup files can be used for analysis and records.
          </Typography>
        </Alert>

        <Box display="flex" gap={2} justifyContent="center">
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CloudDownload />}
            onClick={handleExportBackup}
            disabled={loading || restoring}
            fullWidth
          >
            Export Backup
          </Button>
          
          <Button
            variant="outlined"
            startIcon={restoring ? <CircularProgress size={20} /> : <CloudUpload />}
            onClick={handleImportBackup}
            disabled={loading || restoring}
            fullWidth
          >
            Import Backup
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BackupDialog;
